import { Injectable } from '@angular/core';
import { IDesignatedContactsService } from './designated-contacts.service.interface';
import { BehaviorSubject, Subject } from 'rxjs';

import {
  DesignatedContact,
  DesignatedContactCreateRequest,
  DesignatedContactPermissionResponse,
  DesignatedContactResponse,
  DesignatedContactsResponse,
  DesignatedContactUpdateRequest,
  UpdateDesignatedContactPermissionsRequest,
} from '#models/db/designated-contact/designated-contact.model';
import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IntermediaryService, MS_DELAY } from '#services/intermediary/intermediary.service';
import { logger } from '#lib/logger';
import { DesignatedContactPermission } from '#models/db/profile/profile.model';

@Injectable({
  providedIn: 'root',
})
export class DesignatedContactsService implements IDesignatedContactsService {
  path = 'designated-contacts';
  permissionsPath = 'designated-contact-permissions';

  createDesignatedContactLoading$ = new Subject<boolean>();
  deleteDesignatedContactLoading$ = new Subject<boolean>();
  getDesignatedContactLoading$ = new Subject<boolean>();
  getDesignatedContactsLoading$ = new Subject<boolean>();
  updateDesignatedContactLoading$ = new Subject<boolean>();
  getDesignatedContactPermissionsLoading$ = new Subject<boolean>();
  updateDesignatedContactPermissionsLoading$ = new Subject<boolean>();

  activeDesignatedContact$ = new BehaviorSubject<DesignatedContact | null>(null);
  designatedContacts$ = new BehaviorSubject<DesignatedContact[] | null>(null);
  currentPermissions$ = new BehaviorSubject<DesignatedContactPermission[] | null>(null);

  constructor(
    private apiRepository: ApiRepositoryService,
    private intermediaryHandler: IntermediaryService
  ) {}

  createDesignatedContact(
    req: DesignatedContactCreateRequest,
    nextFn?: (data: DesignatedContact | null) => void
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.createDesignatedContactLoading$,
        subject: this.activeDesignatedContact$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.post<
          DesignatedContactResponse,
          DesignatedContactCreateRequest
        >({ url: this.path }, req);

        logger.log(response, 'DesignatedContactsService.createDesignatedContact -> response');

        return response.details;
      }
    );
  }

  deleteDesignatedContact(id: string, nextFn?: (data: DesignatedContact | null) => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.deleteDesignatedContactLoading$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.delete<DesignatedContactResponse>({
          url: `${this.path}/${id}`,
        });

        const current = this.designatedContacts$.value;

        if (current) {
          this.designatedContacts$.next(
            current.filter((contact) => contact.id !== response.details.id)
          );
        }

        logger.log(response, 'DesignatedContactsService.deleteDesignatedContact -> response');

        return response.details;
      }
    );
  }

  getDesignatedContacts(nextFn?: (data: DesignatedContact[] | null) => void, silent?: boolean) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getDesignatedContactsLoading$ : undefined,
        subject: this.designatedContacts$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<DesignatedContactsResponse>({
          url: this.path,
        });

        logger.log(response.details, 'DesignatedContactsService.getDesignatedContacts -> response');

        return response.details;
      }
    );
  }

  getDesignatedContactById(
    id: string,
    nextFn?: (data: DesignatedContact | null) => void,
    silent?: boolean
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getDesignatedContactLoading$ : undefined,
        subject: this.activeDesignatedContact$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<DesignatedContactResponse>({
          url: `${this.path}/${id}`,
        });

        logger.log(
          response.details,
          'DesignatedContactsService.getDesignatedContactById -> response'
        );

        return response.details;
      }
    );
  }

  updateDesignatedContact(
    req: DesignatedContactUpdateRequest,
    nextFn?: (data: DesignatedContact | null) => void
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.updateDesignatedContactLoading$,
        subject: this.activeDesignatedContact$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.patch<
          DesignatedContactResponse,
          DesignatedContactUpdateRequest
        >(
          {
            url: `${this.path}/${req.id}`,
          },
          req
        );

        logger.log(response, 'DesignatedContactsService.updateDesignatedContact -> response');

        return response.details;
      }
    );
  }

  getDesignatedContactPermissions(
    nextFn?: (data: DesignatedContactPermission[] | null) => void,
    silent?: boolean
  ): void {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getDesignatedContactPermissionsLoading$ : undefined,
        subject: this.currentPermissions$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<DesignatedContactPermissionResponse>({
          url: this.permissionsPath,
        });

        logger.log(
          response.details,
          'DesignatedContactsService.getDesignatedContactPermissions -> response'
        );

        return response.details;
      }
    );
  }

  updateDesignatedContactPermissions(
    payload: UpdateDesignatedContactPermissionsRequest,
    nextFn?: (data: DesignatedContactPermission[] | null) => void
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.updateDesignatedContactPermissionsLoading$,
        subject: this.currentPermissions$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.patch<
          DesignatedContactPermissionResponse,
          UpdateDesignatedContactPermissionsRequest
        >(
          {
            url: this.permissionsPath,
          },
          payload
        );

        logger.log(
          response.details,
          'DesignatedContactsService.updateDesignatedContactPermissions -> response'
        );

        return response.details;
      }
    );
  }

  setActiveDesignatedContact(dc: DesignatedContact | null) {
    this.activeDesignatedContact$.next(dc);
  }

  resetDesignatedContacts() {
    this.designatedContacts$.next(null);
    this.activeDesignatedContact$.next(null);
    this.currentPermissions$.next(null);
    logger.log('complete', 'DesignatedContactsService.resetDesignatedContacts');
  }
}
